import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/SEOHead";

interface RoadmapItem {
  id: string;
  title: string;
  description: string | null;
  status: string;
  display_order: number;
  created_at: string;
}

interface RoadmapSetting {
  status: string;
  colour: string;
}

const STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  planned: "Planned",
  up_next: "Up Next",
  released: "Released",
};

const STATUS_ORDER = ["submitted", "planned", "up_next", "released"];

export default function Roadmap() {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({
    submitted: "#6B7280",
    planned: "#8B5CF6",
    up_next: "#F59E0B",
    released: "#10B981",
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("submitted");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsResult, settingsResult] = await Promise.all([
        supabase
          .from("roadmap_items")
          .select("*")
          .order("display_order", { ascending: true }),
        supabase.from("roadmap_settings").select("*"),
      ]);

      if (itemsResult.data) {
        setItems(itemsResult.data);
      }

      if (settingsResult.data) {
        const settingsMap: Record<string, string> = {};
        settingsResult.data.forEach((s: RoadmapSetting) => {
          settingsMap[s.status] = s.colour;
        });
        setSettings((prev) => ({ ...prev, ...settingsMap }));
      }
    } catch (error) {
      console.error("Error fetching roadmap data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getItemsByStatus = (status: string) => {
    return items.filter((item) => item.status === status);
  };

  const getItemCount = (status: string) => {
    return getItemsByStatus(status).length;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead slug="roadmap" />
      <div className="container mx-auto px-4 py-12 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Product Roadmap</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See what we're working on and what's coming next. We update this
            regularly to keep you informed about new features and improvements.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start mb-8 flex-wrap h-auto gap-2 bg-transparent border-b rounded-none p-0">
            {STATUS_ORDER.map((status) => (
              <TabsTrigger
                key={status}
                value={status}
                className="relative data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-current transition-colors"
                style={{
                  color: activeTab === status ? settings[status] : undefined,
                  borderColor: activeTab === status ? settings[status] : "transparent",
                }}
              >
                <span className="flex items-center gap-2">
                  {STATUS_LABELS[status]}
                  <span
                    className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium"
                    style={{
                      backgroundColor: `${settings[status]}20`,
                      color: settings[status],
                    }}
                  >
                    {getItemCount(status)}
                  </span>
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {STATUS_ORDER.map((status) => (
            <TabsContent key={status} value={status}>
              {getItemsByStatus(status).length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-muted-foreground">
                    No items in this section yet.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {getItemsByStatus(status).map((item) => (
                    <Card
                      key={item.id}
                      className="border-l-4"
                      style={{ borderLeftColor: settings[status] }}
                    >
                      <CardHeader>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                      </CardHeader>
                      {item.description && (
                        <CardContent>
                          <p className="text-muted-foreground text-sm">
                            {item.description}
                          </p>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </>
  );
}
